import Cookies from 'js-cookie';
import moment from 'moment';
import type { AuthenticationResult } from '../types';
import {
  getTokensFromStorage,
  setTokensToStorage,
  removeTokensFromStorage,
  isInIframe,
} from '../utils/iframe-storage.utils';

/**
 * Set tokens with support for iframe scenarios
 * Uses localStorage for iframes, falls back to cookies
 */
export const setToken = async (AuthenticationResult: AuthenticationResult) => {
  const loginDate = moment().format('YYYY-MM-DD HH:mm:ss');
  const tokens = {
    qboIdToken: AuthenticationResult.IdToken,
    qboAccessToken: AuthenticationResult.AccessToken,
    qboRefreshToken: AuthenticationResult.RefreshToken,
    qboLoginDate: loginDate,
  };

  // Always try iframe storage first if in iframe
  if (isInIframe()) {
    setTokensToStorage(tokens);
  }

  // Also save to cookies as fallback (works in non-iframe scenarios)
  try {
    Cookies.set('qboIdToken', AuthenticationResult.IdToken);
    Cookies.set('qboAccessToken', AuthenticationResult.AccessToken);
    if (AuthenticationResult.RefreshToken) {
      Cookies.set('qboRefreshToken', AuthenticationResult.RefreshToken);
    }
    Cookies.set('qboLoginDate', loginDate);
  } catch (e) {
    // Cookies might fail in iframe, that's okay
    console.warn('Failed to set cookies (may be in iframe):', e);
  }
};

/**
 * Get ID token with iframe support
 */
export const getIdToken = async (): Promise<string | undefined> => {
  // Try iframe storage first
  if (isInIframe()) {
    const tokens = await getTokensFromStorage();
    if (tokens?.qboIdToken) {
      return tokens.qboIdToken;
    }
  }

  // Fallback to cookies
  try {
    return Cookies.get('qboIdToken');
  } catch {
    return undefined;
  }
};

/**
 * Get access token with iframe support
 */
export const getAccessToken = async (): Promise<string | undefined> => {
  // Try iframe storage first
  if (isInIframe()) {
    const tokens = await getTokensFromStorage();
    if (tokens?.qboAccessToken) {
      return tokens.qboAccessToken;
    }
  }

  // Fallback to cookies
  try {
    return Cookies.get('qboAccessToken');
  } catch {
    return undefined;
  }
};

/**
 * Get refresh token with iframe support
 */
export const getRefreshToken = async (): Promise<string | undefined> => {
  // Try iframe storage first
  if (isInIframe()) {
    const tokens = await getTokensFromStorage();
    if (tokens?.qboRefreshToken) {
      return tokens.qboRefreshToken;
    }
  }

  // Fallback to cookies
  try {
    return Cookies.get('qboRefreshToken');
  } catch {
    return undefined;
  }
};

/**
 * Get authentication result with iframe support
 */
export const getAuthenticationResult = async (): Promise<AuthenticationResult> => {
  // Try iframe storage first
  if (isInIframe()) {
    const tokens = await getTokensFromStorage();
    if (tokens) {
      return {
        IdToken: tokens.qboIdToken ?? '',
        AccessToken: tokens.qboAccessToken ?? '',
        RefreshToken: tokens.qboRefreshToken ?? '',
        LoginDate: tokens.qboLoginDate ?? '',
      };
    }
  }

  // Fallback to cookies
  try {
    return {
      IdToken: Cookies.get('qboIdToken') ?? '',
      AccessToken: Cookies.get('qboAccessToken') ?? '',
      RefreshToken: Cookies.get('qboRefreshToken') ?? '',
      LoginDate: Cookies.get('qboLoginDate') ?? '',
    };
  } catch {
    return {
      IdToken: '',
      AccessToken: '',
      RefreshToken: '',
      LoginDate: '',
    };
  }
};

/**
 * Remove tokens from all storage locations
 */
export const removeToken = () => {
  // Remove from iframe storage
  if (isInIframe()) {
    removeTokensFromStorage();
  }

  // Remove from cookies
  try {
    Cookies.remove('qboIdToken');
    Cookies.remove('qboAccessToken');
    Cookies.remove('qboRefreshToken');
    Cookies.remove('qboLoginDate');
  } catch (e) {
    // Cookies might fail in iframe, that's okay
    console.warn('Failed to remove cookies (may be in iframe):', e);
  }
};

// Synchronous versions for backward compatibility (will be deprecated)
// These are kept for places that need immediate synchronous access
// but will try to read from localStorage first

export const getIdTokenSync = (): string | undefined => {
  try {
    if (isInIframe()) {
      const tokensJson = localStorage.getItem('qbo_qbo_tokens');
      if (tokensJson) {
        const tokens = JSON.parse(tokensJson);
        if (tokens?.qboIdToken) {
          return tokens.qboIdToken;
        }
      }
    }
  } catch {
    // Fall through to cookies
  }

  try {
    return Cookies.get('qboIdToken');
  } catch {
    return undefined;
  }
};

export const getAccessTokenSync = (): string | undefined => {
  try {
    if (isInIframe()) {
      const tokensJson = localStorage.getItem('qbo_qbo_tokens');
      if (tokensJson) {
        const tokens = JSON.parse(tokensJson);
        if (tokens?.qboAccessToken) {
          return tokens.qboAccessToken;
        }
      }
    }
  } catch {
    // Fall through to cookies
  }

  try {
    return Cookies.get('qboAccessToken');
  } catch {
    return undefined;
  }
};

export const getRefreshTokenSync = (): string | undefined => {
  try {
    if (isInIframe()) {
      const tokensJson = localStorage.getItem('qbo_qbo_tokens');
      if (tokensJson) {
        const tokens = JSON.parse(tokensJson);
        if (tokens?.qboRefreshToken) {
          return tokens.qboRefreshToken;
        }
      }
    }
  } catch {
    // Fall through to cookies
  }

  try {
    return Cookies.get('qboRefreshToken');
  } catch {
    return undefined;
  }
};
